/* ============================================================
   INITIAL STATE — load persisted prefs, then derive the boot
   presentation. Mirrors the prototype's renderInitial() decision
   tree (always boot LOCKED on the no-signal channel, cue the
   resume target underneath) but expressed declaratively.
   ============================================================ */
import { DEFAULT_SETTINGS, type Channel, type PersistedTvState, type TvState } from './types'
import { queueIndexToProgramOrdinal } from './breakResolver'
import { GUIDE_ZOOM_DEFAULT } from './live'
import { parseUrl } from './url'

const STORE_KEY = 'orion.tv.state.v1'
/** Standalone flag (outside the main store blob) for the dismissed extension hint. */
export const EXTENSION_HINT_KEY = 'hideExtensionHint'

const PERSIST_DEFAULTS: PersistedTvState = {
  // A first-ever visit boots OFF — the viewer turns the set on (on-screen power
  // button or remote). Returning visitors restore their saved power state.
  power: 'off',
  channelNum: 1,
  programIndex: 0,
  volume: 70,
  muted: false,
  subtitles: false,
  remote: null,
  remoteScale: 1,
  settings: DEFAULT_SETTINGS,
  unlockedSecrets: [],
}

/** True when there is no persisted state — the user has never visited before. */
export function isFirstVisit(): boolean {
  return localStorage.getItem(STORE_KEY) === null
}

export function loadPersisted(): PersistedTvState {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || '{}')
    // merge settings field-by-field so a newly-added toggle gets its default
    return { ...PERSIST_DEFAULTS, ...raw, settings: { ...DEFAULT_SETTINGS, ...(raw.settings || {}) } }
  } catch {
    return { ...PERSIST_DEFAULTS }
  }
}

export function savePersisted(state: TvState, channels: Channel[]) {
  const persisted: PersistedTvState = {
    power: state.power,
    channelNum: channels[state.channelIndex]?.number ?? 1,
    programIndex: state.queue.length
      ? queueIndexToProgramOrdinal(state.queue, state.programIndex)
      : state.programIndex,
    volume: state.volume,
    muted: state.muted,
    subtitles: state.subtitles,
    remote: state.remotePos,
    remoteScale: state.remoteScale,
    settings: state.settings,
    unlockedSecrets: state.unlockedSecrets,
  }
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(persisted))
  } catch {
    /* storage unavailable; ignore */
  }
}

const noSignalIdx = (channels: Channel[]) => channels.findIndex((c) => c.noSignal)
const firstVideoIdx = (channels: Channel[]) => {
  const i = channels.findIndex((c) => !c.noSignal)
  return i < 0 ? 0 : i
}
const indexOfNumber = (channels: Channel[], num: number) => {
  const i = channels.findIndex((c) => c.number === num)
  return i < 0 ? 0 : i
}

/**
 * Build the boot TvState from persisted prefs + the lineup. Audio is ALWAYS
 * locked at boot (a per-document, per-gesture permission can't survive a
 * refresh). If a no-signal channel exists we boot on it showing snow, and stash
 * the resume target (the channel+program the user last watched) so the whack
 * resumes there.
 */
export function buildInitialState(
  channels: Channel[],
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : '/',
): TvState {
  const p = loadPersisted()

  // A valid /channel/... URL OVERRIDES localStorage on boot (follow-a-link starts watching).
  const route = parseUrl(pathname, channels)
  if (route) {
    p.power = 'on'
    p.channelNum = route.channelNum
    p.programIndex = route.programOrdinal
    // visiting a hidden channel by URL UNLOCKS it (so it then appears in the guide).
    const tc = channels.find((c) => c.number === route.channelNum)
    if (tc?.hidden && !p.unlockedSecrets.includes(tc.number)) p.unlockedSecrets = [...p.unlockedSecrets, tc.number]
  }

  // where the user wants to resume (skip the no-signal channel as a resume target — UNLESS the URL
  // explicitly targets it, e.g. /channel/hideo).
  let resumeIdx = indexOfNumber(channels, p.channelNum)
  if (!route && channels[resumeIdx]?.noSignal) resumeIdx = firstVideoIdx(channels)

  const base: TvState = {
    power: p.power,
    channelIndex: resumeIdx,
    programIndex: p.programIndex || 0,
    volume: p.volume,
    muted: p.muted,
    subtitles: p.subtitles,
    remotePos: p.remote,
    remoteScale: p.remoteScale || 1,
    audio: 'locked',
    screen: 'off',
    bumper: false,
    playback: 'idle',
    activeComponent: null,
    guideOpen: false,
    guideSel: resumeIdx,
    guideProgram: 0,
    guideZoom: GUIDE_ZOOM_DEFAULT,
    guideSearch: null,
    guideSearchSel: 0,
    bootResume: null,
    live: false,
    screenInteractive: false,
    blocked: false,
    osd: { channel: false, banner: false, volume: false },
    osdOverride: null,
    offAirNumber: null,
    fullscreen: false,
    nativeFs: false,
    remoteFs: 'hidden',
    remoteSheetOpen: false,
    remoteCollapsed: false,
    touchScreen: 'dial',
    settings: p.settings,
    adBumperConfirm: null,
    youtubeCleaner: null,
    extensionHintDismissed: localStorage.getItem(EXTENSION_HINT_KEY) === '1',
    bugReport: false,
    queue: [],
    shuffle: false,
    unlockedSecrets: p.unlockedSecrets || [],
  }

  if (p.power === 'off') return base

  const nsIdx = noSignalIdx(channels)
  if (nsIdx >= 0) {
    // boot on CH01 snow, holding the resume target cued underneath
    return {
      ...base,
      channelIndex: nsIdx,
      guideSel: nsIdx,
      screen: 'snow',
      audio: 'awaiting-gesture',
      bootResume: { channelIndex: resumeIdx, programIndex: p.programIndex || 0, live: route?.live },
    }
  }

  // no no-signal channel defined → gate on the first real channel's bumper
  return {
    ...base,
    screen: 'tuning',
    bumper: true,
    audio: 'awaiting-gesture',
    bootResume: { channelIndex: resumeIdx, programIndex: p.programIndex || 0, live: route?.live },
  }
}
